// Beleg von Hand ausstellen — Rechnung oder Lieferschein.
//
// Der Katalog liegt als Kachel-Palette unter der Positionsliste. Antippen legt
// die Position an, ein zweites Antippen erhöht die Menge.
//
// ══ Warum Antippen und nicht nur Ziehen ══
// Das Vorbild (PITLANE QuoteBuilder) zieht Blöcke per HTML5-Drag&Drop in die
// Rubriken. Das ist am Schreibtisch großartig — und auf dem Handy tot: `dragstart`
// feuert auf Touch-Geräten schlicht nicht. Diese App wird auf dem Handy bedient.
// Also: Tippen ist der Hauptweg (funktioniert überall), Ziehen gibt es am
// Desktop zusätzlich obendrauf. Wer eine Maus hat, darf ziehen; wer keine hat,
// steht nicht dumm da.

import { useMemo, useState } from "react";
import { useProducts } from "@/lib/db";
import {
  useBelegErstellen,
  useBelegSenden,
  useKundenSuche,
  type BelegTyp,
} from "@/lib/belege";
import { berechneSummen, zeilenSumme, type BelegPosition } from "@/lib/beleg-summen";
import { Button, Card, Field, Input, Segmented, Sheet, cn, euro, qtyFmt } from "@/design-system/ui";

interface Kunde {
  id: string;
  display_name: string;
  customer_number: string | null;
  email: string | null;
  addresses: { street: string; zip: string | null; city: string | null }[];
}

export function BelegEditor({ onClose, startTyp }: { onClose: () => void; startTyp: BelegTyp }) {
  const [typ, setTyp] = useState<BelegTyp>(startTyp);
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [positionen, setPositionen] = useState<BelegPosition[]>([]);
  const [versandEuro, setVersandEuro] = useState("");
  const [notiz, setNotiz] = useState("");
  const [bezahlt, setBezahlt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<{ id: string; nummer: string; pdf_url: string } | null>(null);

  const erstellen = useBelegErstellen();
  const istRechnung = typ === "rechnung";

  const versandCents = useMemo(() => {
    const n = Number(versandEuro.replace(",", "."));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
  }, [versandEuro]);

  const summen = useMemo(
    () => berechneSummen(positionen, istRechnung ? versandCents : 0),
    [positionen, versandCents, istRechnung]
  );

  /** Dasselbe Produkt zweimal antippen erhöht die Menge, statt eine zweite Zeile anzulegen. */
  const hinzufuegen = (p: {
    id: string;
    name: string;
    unit: string;
    price_cents: number;
    vat_rate: number;
  }) => {
    setPositionen((alt) => {
      const i = alt.findIndex((x) => x.product_id === p.id);
      if (i >= 0) {
        const next = [...alt];
        next[i] = { ...next[i], qty: next[i].qty + (p.unit === "kg" ? 2.5 : 1) };
        return next;
      }
      return [
        ...alt,
        {
          product_id: p.id,
          description: p.name,
          qty: p.unit === "kg" ? 2.5 : 1,
          unit: p.unit,
          unit_price_cents: p.price_cents,
          vat_rate: Number(p.vat_rate),
        },
      ];
    });
  };

  const aendern = (i: number, patch: Partial<BelegPosition>) =>
    setPositionen((alt) => alt.map((p, x) => (x === i ? { ...p, ...patch } : p)));

  const entfernen = (i: number) => setPositionen((alt) => alt.filter((_, x) => x !== i));

  const speichern = async () => {
    setFehler(null);
    if (!kunde) return setFehler("Bitte zuerst einen Kunden wählen.");
    if (!positionen.length) return setFehler("Der Beleg hat keine Positionen.");

    try {
      const r = await erstellen.mutateAsync({
        typ,
        customer_id: kunde.id,
        positionen,
        versand_cents: istRechnung ? versandCents : 0,
        notiz: notiz.trim() || null,
        bezahlt: istRechnung ? bezahlt : false,
      });
      setFertig({ id: r.id, nummer: r.nummer, pdf_url: r.pdf_url });
    } catch (e) {
      setFehler((e as Error).message);
    }
  };

  if (fertig) {
    return (
      <FertigSheet
        typ={typ}
        beleg={fertig}
        kunde={kunde}
        onClose={() => {
          setFertig(null);
          onClose();
        }}
      />
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={istRechnung ? "Neue Rechnung" : "Neuer Lieferschein"}
      footer={
        <div className="space-y-3">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}

          {istRechnung && positionen.length > 0 && (
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink-400">Gesamt</span>
              <span className="text-xl font-semibold tabular-nums text-ink-50">
                {euro(summen.bruttoCents)}
              </span>
            </div>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={erstellen.isPending}
            disabled={!kunde || positionen.length === 0}
            onClick={speichern}
          >
            {istRechnung ? "Rechnung erstellen" : "Lieferschein erstellen"}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <Segmented
          value={typ}
          onChange={(v) => setTyp(v)}
          options={[
            { value: "rechnung", label: "Rechnung" },
            { value: "lieferschein", label: "Lieferschein" },
          ]}
        />

        <KundenWahl kunde={kunde} onWaehlen={setKunde} />

        {/* ── Positionen */}
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-ink-400">
              Positionen
            </span>
            {positionen.length > 0 && (
              <span className="text-xs tabular-nums text-ink-500">
                {positionen.length} {positionen.length === 1 ? "Position" : "Positionen"}
              </span>
            )}
          </div>

          {positionen.length === 0 ? (
            <div
              // Am Desktop darf man Blöcke auch hineinziehen — auf Touch feuert
              // dragstart nicht, deshalb ist Antippen der Hauptweg.
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const roh = e.dataTransfer.getData("produkt");
                if (roh) hinzufuegen(JSON.parse(roh));
              }}
              className="rounded-xl border border-dashed border-ink-700 px-4 py-8 text-center"
            >
              <p className="text-sm text-ink-400">Produkt antippen, um es aufzunehmen</p>
              <p className="mt-1 hidden text-xs text-ink-500 sm:block">
                oder einen Block hierher ziehen
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {positionen.map((p, i) => (
                <li key={`${p.product_id ?? "frei"}-${i}`}>
                  <PositionsZeile
                    p={p}
                    istRechnung={istRechnung}
                    onAendern={(patch) => aendern(i, patch)}
                    onEntfernen={() => entfernen(i)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <Katalog onWaehlen={hinzufuegen} />

        {/* ── Versand + Zahlung (nur Rechnung) */}
        {istRechnung && (
          <div className="space-y-4">
            <Field label="Versandkosten" optional hint="Brutto, wie alles andere auch">
              <Input
                inputMode="decimal"
                placeholder="0,00"
                value={versandEuro}
                onChange={(e) => setVersandEuro(e.target.value)}
              />
            </Field>

            <button
              onClick={() => setBezahlt((b) => !b)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left",
                "transition-colors duration-fast",
                bezahlt
                  ? "border-gruen-500/50 bg-gruen-500/10"
                  : "border-ink-700 bg-ink-900 hover:border-ink-600"
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                  bezahlt ? "border-gruen-500 bg-gruen-500 text-white" : "border-ink-600"
                )}
              >
                {bezahlt && "✓"}
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-medium text-ink-100">
                  Schon bezahlt
                </span>
                <span className="block text-sm text-ink-400">
                  Bar kassiert — die Rechnung gilt sofort als beglichen
                </span>
              </span>
            </button>
          </div>
        )}

        <Field label="Notiz auf dem Beleg" optional>
          <Input
            placeholder="z. B. Lieferung ab Hof"
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
          />
        </Field>

        {/* ── Summenblock */}
        {istRechnung && positionen.length > 0 && (
          <Card className="space-y-1.5 p-4">
            {summen.versandCents > 0 && (
              <SummenZeile
                label="Zwischensumme"
                wert={euro(summen.bruttoCents - summen.versandCents)}
              />
            )}
            {summen.versandCents > 0 && (
              <SummenZeile label="Versand" wert={euro(summen.versandCents)} />
            )}
            <SummenZeile label="Netto" wert={euro(summen.nettoMitVersandCents)} />
            {summen.nachSatz.map((s) => (
              <SummenZeile
                key={s.satz}
                label={`zzgl. ${qtyFmt(s.satz)} % MwSt.`}
                wert={euro(s.mwstCents)}
              />
            ))}
            <div className="!mt-3 border-t border-ink-700 pt-3">
              <SummenZeile label="Gesamt" wert={euro(summen.bruttoCents)} stark />
            </div>
          </Card>
        )}
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════ Kunde

function KundenWahl({
  kunde,
  onWaehlen,
}: {
  kunde: Kunde | null;
  onWaehlen: (k: Kunde | null) => void;
}) {
  const [suche, setSuche] = useState("");
  const { data: treffer = [] } = useKundenSuche(suche);

  if (kunde) {
    const a = kunde.addresses?.[0];
    return (
      <Card className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-ink-100">
            {kunde.display_name}
          </div>
          <div className="mt-0.5 truncate text-sm text-ink-400">
            {[a?.street, [a?.zip, a?.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ") ||
              "keine Adresse hinterlegt"}
          </div>
          {kunde.customer_number && (
            <div className="mt-0.5 font-mono text-xs text-ink-500">{kunde.customer_number}</div>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => {
            onWaehlen(null);
            setSuche("");
          }}
        >
          Ändern
        </Button>
      </Card>
    );
  }

  return (
    <Field label="Kunde" hint="Name oder Kundennummer">
      <Input
        placeholder="Suchen …"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        autoFocus
      />
      {treffer.length > 0 && (
        <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
          {treffer.map((k) => (
            <li key={k.id}>
              <button
                onClick={() => onWaehlen(k as Kunde)}
                className="w-full rounded-lg border border-ink-700 bg-ink-900 px-3 py-2.5 text-left transition-colors duration-fast hover:border-ink-600 hover:bg-ink-800"
              >
                <span className="block truncate text-[15px] text-ink-100">{k.display_name}</span>
                <span className="block truncate text-sm text-ink-500">
                  {k.customer_number ?? "—"} · {k.addresses?.[0]?.street ?? "keine Adresse"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
}

// ═══════════════════════════════════════════════════════ Katalog

function Katalog({
  onWaehlen,
}: {
  onWaehlen: (p: {
    id: string;
    name: string;
    unit: string;
    price_cents: number;
    vat_rate: number;
  }) => void;
}) {
  const { data: produkte = [] } = useProducts();

  const gruppen = useMemo(() => {
    const m = new Map<string, typeof produkte>();
    for (const p of produkte) {
      if (!p.is_active) continue;
      const k = p.category ?? "Sonstiges";
      m.set(k, [...(m.get(k) ?? []), p]);
    }
    return [...m.entries()];
  }, [produkte]);

  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400">
        Produkte
      </div>

      <div className="space-y-4">
        {gruppen.map(([kategorie, liste]) => (
          <div key={kategorie}>
            <div className="mb-1.5 text-xs text-ink-500">{kategorie}</div>
            <div className="grid grid-cols-2 gap-2">
              {liste.map((p) => (
                <button
                  key={p.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      "produkt",
                      JSON.stringify({
                        id: p.id,
                        name: p.name,
                        unit: p.unit,
                        price_cents: p.price_cents,
                        vat_rate: p.vat_rate,
                      })
                    );
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  onClick={() =>
                    onWaehlen({
                      id: p.id,
                      name: p.name,
                      unit: p.unit,
                      price_cents: p.price_cents,
                      vat_rate: Number(p.vat_rate),
                    })
                  }
                  className={cn(
                    "flex min-h-[3.5rem] flex-col justify-center rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 text-left",
                    "transition-colors duration-fast active:scale-[0.98]",
                    "hover:border-gruen-500/50 hover:bg-gruen-500/5",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
                  )}
                >
                  <span className="truncate text-sm font-medium text-ink-100">{p.name}</span>
                  <span className="mt-0.5 text-xs tabular-nums text-ink-400">
                    {euro(p.price_cents)} / {p.unit === "kg" ? "kg" : p.unit === "glas" ? "Glas" : "St."}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════ Position

function PositionsZeile({
  p,
  istRechnung,
  onAendern,
  onEntfernen,
}: {
  p: BelegPosition;
  istRechnung: boolean;
  onAendern: (patch: Partial<BelegPosition>) => void;
  onEntfernen: () => void;
}) {
  const schritt = p.unit === "kg" ? 2.5 : 1;

  return (
    <Card className="p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-ink-100">
          {p.description}
        </span>
        <button
          onClick={onEntfernen}
          aria-label={`${p.description} entfernen`}
          className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-500 transition-colors duration-fast hover:bg-ink-800 hover:text-danger-fg"
        >
          ✕
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2">
        {/* Stepper — darf NIE schrumpfen, sonst verschwindet das Plus.
            (Genau das ist passiert: Input trägt in der Basisklasse `w-full`,
            und cn() ist ein reiner String-Join ohne tailwind-merge — ein
            `w-24` von außen verliert gegen `w-full`. Deshalb steckt das
            Preisfeld jetzt in einem Wrapper mit fester Breite, statt seine
            eigene Breite überschreiben zu wollen.) */}
        <div className="inline-flex h-11 shrink-0 items-stretch overflow-hidden rounded-lg border border-ink-700 bg-ink-900">
          <button
            onClick={() => onAendern({ qty: Math.max(schritt, p.qty - schritt) })}
            className="w-9 text-ink-300 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-50"
            aria-label="weniger"
          >
            −
          </button>
          <span className="flex min-w-[3.75rem] items-center justify-center gap-1 border-x border-ink-700 px-1.5 text-[15px] font-semibold tabular-nums text-ink-50">
            {qtyFmt(p.qty)}
            <span className="text-xs font-normal text-ink-400">
              {p.unit === "kg" ? "kg" : p.unit === "glas" ? "Glas" : "St."}
            </span>
          </span>
          <button
            onClick={() => onAendern({ qty: p.qty + schritt })}
            className="w-9 text-ink-300 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-50"
            aria-label="mehr"
          >
            +
          </button>
        </div>

        {istRechnung && (
          <>
            <div className="w-[4.5rem] shrink-0">
              <Input
                inputMode="decimal"
                aria-label={`Einzelpreis ${p.description}`}
                className="px-2 text-right"
                value={(p.unit_price_cents / 100).toFixed(2).replace(".", ",")}
                onChange={(e) => {
                  const n = Number(e.target.value.replace(",", "."));
                  if (Number.isFinite(n) && n >= 0) {
                    onAendern({ unit_price_cents: Math.round(n * 100) });
                  }
                }}
              />
            </div>
            <span className="ml-auto shrink-0 whitespace-nowrap text-[15px] font-semibold tabular-nums text-ink-50">
              {euro(zeilenSumme(p))}
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

function SummenZeile({
  label,
  wert,
  stark,
}: {
  label: string;
  wert: string;
  stark?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn("text-sm", stark ? "font-medium text-ink-200" : "text-ink-400")}>
        {label}
      </span>
      <span
        className={cn(
          "tabular-nums",
          stark ? "text-lg font-semibold text-ink-50" : "text-sm text-ink-200"
        )}
      >
        {wert}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════ Nach dem Erstellen

function FertigSheet({
  typ,
  beleg,
  kunde,
  onClose,
}: {
  typ: BelegTyp;
  beleg: { id: string; nummer: string; pdf_url: string };
  kunde: Kunde | null;
  onClose: () => void;
}) {
  const senden = useBelegSenden();
  const [gesendet, setGesendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const hatMail = !!kunde?.email?.trim();

  return (
    <Sheet
      open
      onClose={onClose}
      title={`${typ === "rechnung" ? "Rechnung" : "Lieferschein"} ${beleg.nummer}`}
      footer={
        <Button variant="primary" size="lg" className="w-full" onClick={onClose}>
          Fertig
        </Button>
      }
    >
      <div className="space-y-4">
        <Card className="border-gruen-500/30 bg-gruen-500/5 p-4">
          <div className="text-[15px] font-medium text-gruen-400">
            {typ === "rechnung" ? "Rechnung erstellt" : "Lieferschein erstellt"}
          </div>
          <div className="mt-0.5 font-mono text-sm text-ink-300">{beleg.nummer}</div>
        </Card>

        <a href={beleg.pdf_url} target="_blank" rel="noopener noreferrer" className="block">
          <Button className="w-full" size="lg">
            PDF öffnen
          </Button>
        </a>

        {fehler && (
          <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
            {fehler}
          </p>
        )}

        {gesendet ? (
          <p className="rounded-lg border border-gruen-500/30 bg-gruen-500/5 px-3 py-2 text-sm text-gruen-400">
            Per E-Mail verschickt an {kunde?.email}
          </p>
        ) : hatMail ? (
          <Button
            className="w-full"
            size="lg"
            loading={senden.isPending}
            onClick={async () => {
              setFehler(null);
              try {
                await senden.mutateAsync({ typ, id: beleg.id });
                setGesendet(true);
              } catch (e) {
                setFehler((e as Error).message);
              }
            }}
          >
            An {kunde?.email} senden
          </Button>
        ) : (
          <p className="text-sm text-ink-500">
            Für {kunde?.display_name ?? "diesen Kunden"} ist keine E-Mail-Adresse hinterlegt — der
            Beleg lässt sich ausdrucken und mitgeben. Trägst du im Kundenstamm eine Adresse nach,
            kann er beim nächsten Mal direkt verschickt werden.
          </p>
        )}
      </div>
    </Sheet>
  );
}
